/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Demo Mode helpers — used across the Workflow Catalog / Run Viewer.
 *
 * Demo Mode hides internal IDs, latency numbers, LLM tier names, and
 * cost details so Lalit can record clean LinkedIn videos. The toggle
 * lives in the page header (DemoModeToggle.jsx, Slice 4); persistence
 * is one localStorage key so the state survives page reloads during a
 * recording session.
 *
 * Functions are deliberately stateless and side-effect-free except for
 * the toggle itself, so they can be safely called in render paths.
 */

const STORAGE_KEY = 'demo_mode_enabled';
const CHANGE_EVENT = 'demo-mode-changed';

export function isDemoMode() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch (_) {
    return false;
  }
}

export function setDemoMode(enabled) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { enabled } }));
  } catch (_) {
    /* ignore */
  }
}

export function toggleDemoMode() {
  const next = !isDemoMode();
  setDemoMode(next);
  return next;
}

/**
 * Subscribe to changes — components that render counts / labels behind
 * demo gates can re-render when the toggle flips.
 */
export function subscribeDemoMode(handler) {
  if (typeof window === 'undefined') return () => {};
  const wrapped = (e) => handler(Boolean(e?.detail?.enabled));
  window.addEventListener(CHANGE_EVENT, wrapped);
  return () => window.removeEventListener(CHANGE_EVENT, wrapped);
}

// ─────────────────────────────────────────────────────────────────────
// Display helpers — used by stage/event/card components.
// In demo mode, we soften numbers and hide ids; otherwise pass through.
// ─────────────────────────────────────────────────────────────────────

const PERSONA_DISPLAY_NAMES = {
  // Frontend-only mapping — V2 owns the canonical persona records, but
  // the demo recording needs polished titles instead of internal labels.
  floor_worker: 'WMS Senior Consultant',
  lalit_wms_operations_lead: 'Lalit Ratwani — WMS Operations Lead',
  deepak_as400_architect: 'Deepak — AS/400 Architect',
};

export function displayPersonaName(personaIdOrName, demo) {
  if (!personaIdOrName) return demo ? 'AI Specialist' : 'Persona';
  if (!demo) return personaIdOrName;
  const key = String(personaIdOrName).toLowerCase().replace(/[.\s-]+/g, '_');
  return PERSONA_DISPLAY_NAMES[key] || personaIdOrName;
}

export function displayLlmTier(tier, demo) {
  if (!demo) return tier || 'unknown';
  return 'AI';
}

export function displayLatency(ms, demo) {
  if (ms == null) return '—';
  if (!demo) return `${ms} ms`;
  // Demo: collapse to a coarse bucket
  if (ms < 1000) return 'Just now';
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Format a cost value for display.
 *
 * V2 may return cost as any of:
 *   - null / undefined        → null
 *   - number  (e.g. 0.003)    → "$0.0030"  / demo "$0.00"
 *   - numeric string ("0.003")→ "$0.0030"
 *   - non-numeric string      → returned as-is (e.g. "$5,000 USD per sprint")
 *   - object { model, amount_usd } → "$0.0030" using amount_usd
 *   - object { amount } / { usd } → ditto, hunting for the numeric field
 *   - anything else           → null (don't render unknown shapes — React
 *                                     would throw error #31 on raw objects)
 */
export function displayCost(value, demo) {
  if (value == null) return null;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return demo ? `$${value.toFixed(2)}` : `$${value.toFixed(4)}`;
  }

  if (typeof value === 'string') {
    if (value.trim() === '') return null;
    const n = Number(value);
    if (Number.isFinite(n)) {
      return demo ? `$${n.toFixed(2)}` : `$${n.toFixed(4)}`;
    }
    // Already a formatted label like "$5,000 USD per sprint" — pass through.
    return value;
  }

  if (typeof value === 'object') {
    const amount = value.amount_usd ?? value.amount ?? value.usd;
    if (typeof amount === 'number' && Number.isFinite(amount)) {
      return demo ? `$${amount.toFixed(2)}` : `$${amount.toFixed(4)}`;
    }
    if (typeof amount === 'string') {
      const n = Number(amount);
      if (Number.isFinite(n)) {
        return demo ? `$${n.toFixed(2)}` : `$${n.toFixed(4)}`;
      }
    }
    return null;
  }

  return null;
}

export function displayRunId(runId, demo) {
  if (!runId) return '';
  if (!demo) return runId;
  return `…${String(runId).slice(-6)}`;
}

export function displayPersonaCount(count, demo) {
  if (count == null) return '—';
  if (!demo) return `${count}`;
  // In demo we still show count, just don't surface internal terminology
  return `${count}`;
}

export function displayToolCount(count, demo) {
  if (count == null) return '—';
  return `${count}`;
}
